import {useState,useEffect,useCallback} from 'react';
import {Paper,Typography,Stack,Button,ButtonGroup,Box,IconButton,Collapse} from '@mui/material';
import {useKeycloak} from "@react-keycloak/web";
import {useApplication} from './ApplicationContext';
import {consoleLog} from '../../core/utilities/Utilities.js';
import CountdownTimer from './Repository/CountdownTimer.js';
import {styled} from '@mui/material/styles';
import {useNavigate} from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import backgroundImage from '../assets/active_task.png';
import TaskDeactivatePopup from './Task/TaskDeactivatePopup.js';
import {invokeObjectMethod} from '../../core/integration/ObjectManager.js';
import {getActiveTaskInfo} from '../integration/Api.js';
import {useSnackbar} from './SnackbarContext.js';
import {useLoading} from './LoadingContext';

const file='Footer.js';
const backgroundStyle={
  backgroundSize: "cover",
  backgroundPosition: "center",
  width: "100%",
  backgroundBlendMode: "overlay",
  backgroundColor: "rgb(248 248 248 / 87%)",
  backgroundImage: `url(${backgroundImage})`,
}

const ExpandMore=styled((props)=>{
  const {expand,...other }=props;
  return <IconButton {...other}/>;
  })(({theme})=>({
    marginLeft:'auto',
    transition:theme.transitions.create('transform',{
      duration:theme.transitions.duration.shortest,
    }),
    variants:[{
      props:({expand})=>!expand,style:{
        transform:'rotate(180deg)',
      },
    },{
      props:({expand})=>!!expand,style:{
        transform:'rotate(0deg)',
      },
    },
  ],
}));

const ColorButtonSmall=styled(Button)(({theme})=>({
  color:'white',
  fontSize:'0.7125rem',
  backgroundColor:'#599485',
  '&:hover':{
    backgroundColor:'#407164',
  },
  '& .MuiButtonGroup-firstButton': {
    borderColor: "black",
  },
  marginLeft:'10px',
  marginBottom:'5px',
  paddingRight:'1',
  paddingTop:'0',
  paddingBottom:'0',
}));

const parseTimeString=(timeStr)=>{
  const [hoursStr,minutesStr]=timeStr.split(':');
  const hours=parseInt(hoursStr,10);
  const minutes=parseInt(minutesStr,10);
  return {hours,minutes};
}

const calculateRemainingTime=(planned_duration_minutes,actual_start_time)=>{
  const actualStart=parseTimeString(actual_start_time);
  const actualStartMinutes=parseInt((actualStart.hours*60)+actualStart.minutes);
  const now=new Date();
  const nowMinutes=now.getHours()*60+now.getMinutes();
  const elapsedMinutes=nowMinutes-actualStartMinutes;
  const remainingMinutes=planned_duration_minutes-elapsedMinutes;
  const hours=Math.floor(remainingMinutes/60);
  const minutes=remainingMinutes%60;
  return {hours,minutes};
}

function Footer({userData}){
  const {keycloak}=useKeycloak();
  const isLoggedIn=keycloak.authenticated;
  const {globalData,updateGlobalData}=useApplication();
  const application={globalData,updateGlobalData,getLocale:(key,options)=>globalData?.getLocale(key,options)};
  const [activeTask,setActiveTask]=useState(null);
  const [expand,setExpand]=useState(true);
  const [initialTime,setInitialTime]=useState({initialHours:null,initialMinutes:null});
  const navigate=useNavigate();
  const [taskDeactivate,setTaskDeactivate]=useState(false);
  const {showSnackbar}=useSnackbar();
  const {showLoading,hideLoading}=useLoading();
  
  const handleCheckActiveTask=useCallback(async()=>{
    const activeTask=await getActiveTaskInfo();
    if(activeTask){
      if(activeTask?.task_id){
        setActiveTask(activeTask);
        const actual_start_time=activeTask?.actual_start_time;
        const planned_duration_minutes=parseInt(activeTask?.planned_duration_minutes);
        const {hours,minutes}=calculateRemainingTime(planned_duration_minutes,actual_start_time);
        setInitialTime((old)=>{
          if(old.initialHours===hours&&old.initialMinutes===minutes){
            return old;
          }
          return {initialHours:hours,initialMinutes:minutes};
        });
      }
      else{
        setActiveTask(null);
      }
    }
  },[]);

  useEffect(()=>{
    if(isLoggedIn){
      handleCheckActiveTask();
    }
    function handleVisbilityEvent(){
      const isCurrentlyVisible=document.visibilityState==='visible'||document.hidden===false;
      if(isCurrentlyVisible&&isLoggedIn){
        handleCheckActiveTask();
      }
    }
    document.addEventListener("visibilitychange",handleVisbilityEvent);
    return()=>{
      document.removeEventListener("visibilitychange",handleVisbilityEvent);
    };
  },[globalData?.activeTask,isLoggedIn,handleCheckActiveTask]);

  const taskDeactivateCallback=(obj)=>{
    setTaskDeactivate(false);
    updateGlobalData({...globalData,activeTask:{}});
  };

  const handleClickEvent=(args)=>{
    switch(args?.action){
        case "deactivate_task":
            deactivateTask();
            break;
        case "task_drilldown":
            navigate('/task/'+activeTask.task_id+'/hours/');
            break
        default: 
            break;
    }
  }

  const deactivateTask=()=>{
    showLoading();
    invokeObjectMethod({entityType:'Task',entityId:activeTask.task_id,entityMethod:'deactivate_task',args:{},_lang:globalData.lang})
    .then((data)=>{
      consoleLog({file:file,context:'deactivateTask - invokeObjectMethod',arg_name:'data',arg_value:data});
      if('error' in data){
        showSnackbar(data.error,'error');
      }
      else if(data.record){
        setTaskDeactivate(true);
      }
      hideLoading();
    });
  }

  const handleExpandClick=()=>{
    setExpand(!expand);
  }
  
  return (
    <>
      {taskDeactivate&&<TaskDeactivatePopup taskDeactivateCallback={taskDeactivateCallback} taskId={activeTask.task_id} application={application} userData={userData}></TaskDeactivatePopup>} 
      {isLoggedIn&&globalData&&
      <div>
        {activeTask&&
        <Paper style={backgroundStyle} sx={{textAlign:'center',zIndex:'1000',position:'fixed',bottom:20,left:0,right:0,backgroundColor:'rgb(253 230 165)'}} elevation={3} square>
          <ExpandMore
            sx={{pb:0,pt:0,pr:0,pl:0,right:'0px',top:'0px',position:'absolute'}}
            expand={expand}
            onClick={handleExpandClick}
            aria-label="show more">
            <ExpandMoreIcon/>
          </ExpandMore>
          <Stack direction={{xs:'column',sm:'column'}} spacing={{xs:1,sm:1,md:1}}>
            <Collapse in={expand} timeout="auto" unmountOnExit>
              <Typography sx={{pb:0}} variant="h5">
                {activeTask.account_name+' ('+activeTask.planned_start_time+'-'+activeTask.planned_end_time+')'}
                <ColorButtonSmall style={{width:'fit-content'}} name="gototask" variant="contained" onClick={()=>{handleClickEvent({action:'task_drilldown'})}}>{globalData.strings.ButtonGoToTask}</ColorButtonSmall>
              </Typography>
              <Typography sx={{pb:0}} variant="h5">
                {globalData.strings.FieldStartTime+': '+activeTask.actual_start_time}
              </Typography>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                <ButtonGroup sx={{textAlign:'center'}} size="small">
                  <Button name="deactivate_task" color="error" variant="contained" onClick={()=>{handleClickEvent({action:'deactivate_task'})}}>{globalData.strings.ButtonDeactivateTask}</Button>
                </ButtonGroup>
              </Box>
            </Collapse>
            <Typography sx={{pb:0}} variant="h5">
              {initialTime.initialHours!==null&&initialTime.initialMinutes!==null&&<CountdownTimer initialHours={initialTime.initialHours} initialMinutes={initialTime.initialMinutes}/>}
            </Typography>
          </Stack>
        </Paper>}
        <Paper sx={{textAlign:'center',zIndex:'1000',position:'fixed',bottom:0,left:0,right:0}} style={{height:'20px'}} elevation={3} square>
          <Typography sx={{pb:0,fontWeight:'200',fontSize:'0.75rem'}} variant="subtitle1">
              {globalData.list_of_values.User_Role[userData.role_name]+': '+userData.user_name+' ('+globalData.strings.FooterUser+': '+userData.user_login+')'}
          </Typography>
        </Paper>
      </div>
      }
    </>
  );
}
export default Footer;
